import React, { useMemo } from 'react';
import Plot from 'react-plotly.js';

import { chartColors } from '../../helpers/chartColors';
import { useSelector } from 'react-redux';
import { selectUser } from '../../reducers/userSlice';
import { selectRTFO } from '../../reducers/rtfoSlice';
import { useTheme } from '@mui/styles';

export const RTFOFlushStepdownChart = ({ gridView, sdtType }) => {

  const theme = useTheme();

  const user = useSelector(selectUser);
  const darkMode = user?.value?.darkMode;
  
  const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;

  const currentStage = currentRTFO?.stageData?.[currentRTFO?.currentStageName];
  

  const data = useMemo(() => {
    const stepdownData = sdtType === 'flush' ? currentStage?.stepdownData : sdtType === 'preSand' ? currentStage?.preSandStepdownData : [];
    const stepdownParams = currentStage?.stepdownParams || {};

    if (!stepdownData || !stepdownParams) return [];
    
    return getChartData();

    function getChartData() {
      try {

        const X = [];
        const XFit = [];
        const YPEntry = [];
        const YPerfFriction = [];
        const YPNwb = [];
        const YPEntryModel = [];

        // calculate pEntryModel fit
        const stepdownFit = [];

        for (let rate = 1; rate <= 18; rate++) {
          const convertedRate = rate * 6.2898; // convert to bbl/min
          const rho = stepdownParams?.rho || 8.3;

          const perfFriction = ((0.2369 * rho) / (((sdtType === 'flush' ? stepdownParams?.Cd : stepdownParams?.preSandCd) * (sdtType === 'flush' ? stepdownParams?.N : (stepdownParams?.preSandNumShots || stepdownParams?.numShots)) * (sdtType === 'flush' ? stepdownParams?.Dperf : stepdownParams?.preSandDperf) ** 2) ** 2) * (convertedRate ** 2)) / 145.038; // convert psi to MPa
          const pNwb = ((sdtType === 'flush' ? stepdownParams?.kNwb : stepdownParams?.preSandKNwb) * (convertedRate ** (sdtType === 'flush' ? stepdownParams?.bNwb : stepdownParams?.preSandBNwb))) / 145.038; // convert psi to MPa
          const pEntryModel = perfFriction + pNwb;

          stepdownFit.push({
            rate,
            perfFriction,
            pNwb,
            pEntryModel
          });
        };

        stepdownData?.forEach(d => {
          X.push(d?.rate || 0);
          YPEntry.push(d?.pEntry || 0);
        });

        stepdownFit?.forEach(d => {
          XFit.push(d?.rate || 0);
          YPerfFriction.push(d?.perfFriction || 0);
          YPNwb.push(d?.pNwb || 0);
          YPEntryModel.push(d?.pEntryModel || 0);
        });

        // add traces
        let pEntryTrace = {
          x: X,
          y: YPEntry,
          mode: 'markers',
          type: 'scattergl',
          xaxis: 'x1',
          yaxis: 'y1',
          name: 'Pentry (MPa)',
          marker: {
            line: {
              width: 3,
              color: chartColors[2]
            },
            color: 'transparent',
            size: 8
          },
        };
        let pPerfFrictionTrace = {
          x: XFit,
          y: YPerfFriction,
          mode: 'lines',
          type: 'scattergl',
          xaxis: 'x1',
          yaxis: 'y1',
          name: 'Pperf (MPa)',
          line: {
            width: 3,
            color: chartColors[4]
          }
        };
        let pNwbTrace = {
          x: XFit,
          y: YPNwb,
          mode: 'lines',
          type: 'scattergl',
          xaxis: 'x1',
          yaxis: 'y1',
          name: 'Pnwb (MPa)',
          line: {
            width: 3,
            color: chartColors[6]
          }
        };
        let pEntryModelTrace = {
          x: XFit,
          y: YPEntryModel,
          mode: 'lines',
          type: 'scattergl',
          xaxis: 'x1',
          yaxis: 'y1',
          name: 'Pmodel (MPa)',
          line: {
            width: 3,
            color: chartColors[2]
          }
        };

        return [ 
          pEntryTrace,
          pEntryModelTrace,
          pPerfFrictionTrace,
          pNwbTrace
        ];
  
      } catch (error) {
        console.log(error);
        return [];
      };
    };
  }, [currentStage?.preSandStepdownData, currentStage?.stepdownData, currentStage?.stepdownParams, sdtType]);

  const layout = {
    yaxis: { // Pressure y axis
      linecolor: chartColors[2],
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: chartColors[2],
      title: {
        text: 'Pressure (MPa)',
        font: {
          size: 12
        },
      },
      anchor: 'x',
      autorange: true,
      side: 'left'
    },
    xaxis: { // Rate x axis
      autorange: true,
      title: {
        text: 'Rate (m3/min)',
        font: {
          size: 12
        }
      },
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      anchor: 'y',
      showspikes: true,
      spikethickness: 2,
      spikedash: "dot",
      spikecolor: "#999999",
      spikemode: "across",
    },
    hovermode: "x",
    hoverdistance: 100, // Distance to show hover label of data point
    spikedistance: 1000,
    autosize: true,
    showlegend: true,
    legend: {
      orientation: "h",
      yanchor: "top",
      font: {
        color: darkMode ? 'lightgrey' : 'grey'
      },
      y: (gridView ? -0.2 : -0.1),
    },
    annotations: [
      {
        text: `${sdtType === 'flush' ? 'Flush' : 'Pre Sand'} SDT`,
          font: {
          size: 14,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: (gridView ? 1.15 : 1.075), //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
      {
        text: `Pad: ${currentRTFO?.name} | Well: ${currentStage?.wellName} | Stage: ${currentStage?.stageNumber || "No stage selected"}`,
          font: {
          size: 10,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: (gridView ? 1.075 : 1.035), //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
    ],
    uirevision: 'true',
    margin: {"t": 60, "l": 60, "r": 60, "b": 50},
    images: [
      {
        x: (gridView ? 0.05 : 0.025),
        y: (gridView ? 1.05 : 1.025),
        sizex: (gridView ? 0.1 : 0.05),
        sizey: 0.1,
        source: process.env.PUBLIC_URL + `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
        xref: "page",
        yref: "y1 domain",
        xanchor: "right",
        yanchor: "bottom",
        opacity: 0.75
      }
    ],
    plot_bgcolor: "transparent",
    paper_bgcolor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]
  };

  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: "RTFO-" + (new Date().toISOString().split('T')[0]),
      // filename: currentFBA?.name + "-" + (new Date().toISOString().split('T')[0]),
      height: 550,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    }
  };

  return (
    <Plot
      data={data}
      layout={layout}
      config={config}
      useResizeHandler={true}
      style={{width: '100%', height: '100%'}}
    />
  );

};

export const RTFOFlushStepdownChartMemo = React.memo(RTFOFlushStepdownChart);